import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  HStack,
  IconButton,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  MdArrowBack,
  MdClose,
  MdLockOpen,
  MdPersonSearch,
  MdStorefront,
} from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AppCard,
  OutlineButton,
  PrimaryButton,
  SortableTh,
  StatusAlertCard,
  StatusBadge,
  PageLoader,
} from '../../../../components/ui';
import { useTableSort } from '../../../../hooks/useTableSort';
import {
  indentStatusBadge,
  operationalBadge,
  resolveAssetOwner,
} from '../../../../data/master/assetOwner';
import {
  assignIndent,
  getIndent,
  listIndents,
  releaseIndent,
} from '../../../../repositories/indent.repository';
import { getConfigValue } from '../../../../repositories/systemConfig.repository';
import { loadByIdOrCode } from '../../../../utils/entityResolve';
import {
  formatDaysPassed,
  formatIndianDateTime,
  formatIndianDateTimeParts,
} from '../../../../utils/datetime';
import { formatDobDisplay } from '../../../../utils/personName';
import { formatTimePeriod } from '../../../../utils/timePeriod';
import { RecommendAssetsModal } from './RecommendAssetsModal';

const DETAIL_COLUMNS = { base: 1, sm: 2, md: 3, lg: 4 };

function DetailRow({ label, value, onClick }) {
  const clickable = typeof onClick === 'function';
  return (
    <Box
      as={clickable ? 'button' : 'div'}
      type={clickable ? 'button' : undefined}
      p={{ base: 3, xl: 4 }}
      bg="bg.input"
      borderRadius="sm"
      borderWidth="1px"
      borderColor="border.subtle"
      w="100%"
      textAlign="left"
      cursor={clickable ? 'pointer' : 'default'}
      onClick={onClick}
      _hover={clickable ? { borderColor: 'utkarsh.amber' } : undefined}
    >
      <Text fontSize="12px" color="text.secondary" mb={1} fontWeight="500">
        {label}
      </Text>
      {typeof value === 'string' || typeof value === 'number' || value == null ? (
        <Text
          fontSize="15px"
          fontWeight="600"
          whiteSpace="pre-wrap"
          lineHeight="1.4"
          color={clickable ? 'accent.amber' : undefined}
        >
          {value || '—'}
        </Text>
      ) : (
        value
      )}
    </Box>
  );
}

function RequestTimingValue({ createdAt }) {
  const parts = formatIndianDateTimeParts(createdAt);
  const daysLabel = formatDaysPassed(createdAt);
  if (!parts.date && !parts.time && !daysLabel) {
    return (
      <Text fontSize="15px" fontWeight="600">
        —
      </Text>
    );
  }
  return (
    <VStack align="start" spacing={1.5} mt={0.5}>
      <Flex gap={1.5} wrap="wrap">
        {parts.date ? <StatusBadge status="neutral">{parts.date}</StatusBadge> : null}
        {parts.time ? <StatusBadge status="info">{parts.time}</StatusBadge> : null}
      </Flex>
      {daysLabel ? (
        <Text fontSize="14px" fontWeight="600">
          Days passed · {daysLabel}
        </Text>
      ) : null}
    </VStack>
  );
}

export default function IndentDetailPage() {
  const { indentId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const pickModal = useDisclosure();
  const [indent, setIndent] = useState(null);
  const [maxRecommendations, setMaxRecommendations] = useState(3);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [loaded, maxRecs] = await Promise.all([
        loadByIdOrCode({
          param: indentId,
          listFn: listIndents,
          getFn: getIndent,
          codeKey: 'indentId',
        }),
        getConfigValue('maxRecommendationsPerRequest'),
      ]);
      setIndent(loaded);
      setMaxRecommendations(Number(maxRecs) || 3);
    } catch (err) {
      toast({
        title: err?.message || 'Failed to load indent',
        status: 'error',
        position: 'top-right',
      });
      setIndent(null);
    } finally {
      setLoading(false);
    }
  }, [indentId, toast]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => load();

  const assetsByCode = useMemo(() => {
    const map = {};
    (indent?.recommendations || []).forEach((r) => {
      if (r.equipment?.assetId) map[r.equipment.assetId] = r.equipment;
    });
    if (indent?.assignment?.equipment?.assetId) {
      map[indent.assignment.equipment.assetId] = indent.assignment.equipment;
    }
    return map;
  }, [indent]);

  const findAssetById = (code) => assetsByCode[code] || null;

  const vendorId = indent?.vendorId || '';
  const vendorName =
    indent?.vendorName || indent?.vendorId || '—';
  const vendorLocalProfessional = indent?.vendorLocalProfessional || '—';

  const assignedAsset = indent?.assignment?.assetId
    ? findAssetById(indent.assignment.assetId)
    : null;
  const assignedOwner = assignedAsset ? resolveAssetOwner(assignedAsset) : null;

  const recommendationRows = useMemo(() => {
    if (!indent) return [];
    return (indent.recommendations || []).map((r) => {
      const asset = r.equipment || findAssetById(r.assetId);
      const owner = resolveAssetOwner(asset);
      return { rec: r, asset, owner };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indent, assetsByCode]);

  const {
    sorted: sortedRecommendations,
    sort,
    toggleSort,
  } = useTableSort(recommendationRows, {
    assetId: (r) => r.rec.assetId,
    equipment: (r) => r.asset?.equipmentDescription || r.asset?.equipmentType,
    ownerType: (r) => r.owner.ownerType,
    owner: (r) => r.owner.ownerName,
    village: (r) => r.owner.villageName,
    operational: (r) => r.asset?.operationalStatus,
    remark: (r) => r.rec.remark || r.rec.note,
  });

  if (loading) {
    return <PageLoader label="Loading indent…" />;
  }

  if (!indent) {
    return (
      <Box>
        <OutlineButton
          size="sm"
          leftIcon={<MdArrowBack />}
          mb={4}
          onClick={() => navigate('/admin/equipments/indents')}
        >
          Back to requests
        </OutlineButton>
        <AppCard py={12} textAlign="center">
          <Text fontWeight="600">Request not found</Text>
          <Text mt={2} color="text.secondary">
            No indent {indentId}.
          </Text>
        </AppCard>
      </Box>
    );
  }

  const minBeforeAssign = 1;
  const slotsLeft = Math.max(0, maxRecommendations - (indent.recommendations?.length || 0));

  const canRecommend =
    indent.status === 'Pending' || indent.status === 'Recommended';
  const canAssign =
    canRecommend && (indent.recommendations?.length || 0) >= minBeforeAssign;
  const isReleased = Boolean(indent.assignment?.releasedAt);
  const canRelease = indent.status === 'Assigned' && !isReleased;

  const onAssign = async (assetCode) => {
    const asset = findAssetById(assetCode);
    const equipmentId = asset?.id || indent.recommendations?.find((r) => r.assetId === assetCode)?.assetDbId;
    if (!indent.id || !equipmentId) {
      toast({ title: 'Missing ids for assign', status: 'error', position: 'top-right' });
      return;
    }
    setBusy(true);
    try {
      await assignIndent(indent.id, { equipmentId });
      await refresh();
      toast({
        title: 'Work assigned',
        description: `${assetCode} marked Deployed for this request.`,
        status: 'success',
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to assign',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setBusy(false);
    }
  };

  const onRelease = async () => {
    const assetId = indent.assignment?.assetId;
    if (!indent.id) return;
    setBusy(true);
    try {
      await releaseIndent(indent.id, { releaseNote: 'Released by Utkarsh Team' });
      await refresh();
      toast({
        title: 'Equipment released',
        description: `${assetId} is Available again.`,
        status: 'success',
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to release',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setBusy(false);
    }
  };

  const onRemoveRecommendation = () => {
    toast({
      title: 'Remove recommendation is not available via API yet',
      status: 'info',
      position: 'top-right',
    });
  };

  return (
    <Box>
      <Flex
        justify="space-between"
        align={{ base: 'start', md: 'center' }}
        gap={4}
        mb={6}
        wrap="wrap"
      >
        <Box>
          <OutlineButton
            size="sm"
            leftIcon={<MdArrowBack />}
            mb={3}
            onClick={() => navigate('/admin/equipments/indents')}
          >
            Back
          </OutlineButton>
          <Heading size={{ base: 'lg', xl: 'xl' }}>
            {indent.line.equipmentType || 'Equipment request'}
            <Text as="span" color="text.secondary" fontWeight="500">
              {' '}
              · {indent.indentId}
            </Text>
          </Heading>
          <Flex align="center" gap={2} mt={2} wrap="wrap">
            <StatusBadge status={indentStatusBadge(indent.status)}>
              {indent.status}
            </StatusBadge>
            <Text color="text.secondary" fontSize="14px">
              {[
                indent.vendorName || indent.vendorId,
                indent.buName || indent.buId,
                indent.siteName || indent.siteId,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Text>
          </Flex>
        </Box>
        <HStack spacing={2} wrap="wrap">
          {canRecommend ? (
            <OutlineButton
              size="sm"
              leftIcon={<MdPersonSearch />}
              isDisabled={slotsLeft === 0}
              onClick={pickModal.onOpen}
            >
              Find &amp; recommend ({slotsLeft} left)
            </OutlineButton>
          ) : null}
          {canRelease ? (
            <PrimaryButton
              size="sm"
              leftIcon={<MdLockOpen />}
              isDisabled={busy}
              onClick={onRelease}
            >
              Release equipment
            </PrimaryButton>
          ) : null}
        </HStack>
      </Flex>

      {indent.status === 'Pending' ? (
        <Box mb={6}>
          <StatusAlertCard
            title="Awaiting recommendations"
            body={`Use Find & recommend to open available vehicles, select up to ${maxRecommendations}, add a remark for each, then Recommend.`}
            icon={MdPersonSearch}
          />
        </Box>
      ) : null}

      {indent.status === 'Recommended' ? (
        <Box mb={6}>
          <StatusAlertCard
            title="Recommendations ready"
            body="Select vehicles with Find & recommend. When the vendor confirms who got the work, click Assign on that row."
            icon={MdPersonSearch}
          />
        </Box>
      ) : null}

      {indent.status === 'Assigned' && assignedOwner ? (
        <Box mb={6}>
          <StatusAlertCard
            title={`Deployed · ${assignedOwner.ownerName}`}
            body={`${assignedAsset?.equipmentDescription || assignedAsset?.equipmentType || ''} ${assignedAsset?.regNo || ''} · ${assignedOwner.ownerType} ${assignedOwner.ownerId}. Release the equipment once the work is finished to make it available again.`}
            icon={MdPersonSearch}
          />
        </Box>
      ) : null}

      {isReleased ? (
        <Box mb={6}>
          <StatusAlertCard
            title="Equipment released"
            body={`${indent.assignment.assetId} was released on ${formatIndianDateTime(indent.assignment.releasedAt) || '—'} by ${indent.assignment.releasedBy || '—'} and is Available again.`}
            icon={MdLockOpen}
          />
        </Box>
      ) : null}

      <AppCard mb={6}>
        <Heading size="sm" mb={4}>
          Request details
        </Heading>
        <SimpleGrid columns={DETAIL_COLUMNS} spacing={3}>
          <DetailRow
            label="Vendor"
            value={vendorName}
            onClick={
              vendorId
                ? () => navigate(`/admin/equipments/vendors/${vendorId}`)
                : undefined
            }
          />
          <DetailRow
            label="LOCAL / PROFESSIONAL"
            value={vendorLocalProfessional}
          />
          <DetailRow
            label="BU · Site"
            value={
              [indent.buName || indent.buId, indent.siteName || indent.siteId]
                .filter(Boolean)
                .join(' · ') || '—'
            }
          />
          <DetailRow
            label="Request given"
            value={<RequestTimingValue createdAt={indent.createdAt} />}
          />
          <DetailRow label="Work area" value={indent.workArea} />
          <DetailRow label="Raised by" value={indent.raisedByName} />
          <DetailRow label="Email" value={indent.raisedByEmail} />
          <DetailRow
            label="Equipment type"
            value={indent.line.equipmentType || '—'}
          />
          <DetailRow label="Specification" value={indent.line.specification} />
          <DetailRow
            label="Qty"
            value={`${indent.line.quantity} ${indent.line.uom || ''}`.trim()}
          />
          <DetailRow
            label="Capacity"
            value={
              indent.line.capacity
                ? `${indent.line.capacity} ${indent.line.capacityUom || ''}`.trim()
                : '—'
            }
          />
          <DetailRow
            label="Required from"
            value={formatDobDisplay(indent.line.requiredFrom)}
          />
          <DetailRow
            label="Required till"
            value={formatDobDisplay(indent.line.requiredTill)}
          />
          <DetailRow
            label="Time period"
            value={
              formatTimePeriod(indent.line.requiredFrom, indent.line.requiredTill) ||
              indent.line.timePeriod
            }
          />
          <DetailRow label="Working hours" value={indent.line.workingHours} />
          <DetailRow label="Remarks" value={indent.line.remarks} />
        </SimpleGrid>
        {vendorId ? (
          <OutlineButton
            mt={4}
            size="sm"
            leftIcon={<MdStorefront />}
            onClick={() => navigate(`/admin/equipments/vendors/${vendorId}`)}
          >
            Open vendor
          </OutlineButton>
        ) : null}
      </AppCard>

      <AppCard mb={6} p={0} overflow="hidden">
        <Box px={{ base: 5, xl: 6 }} pt={{ base: 5, xl: 6 }} pb={3}>
          <Flex justify="space-between" align="start" gap={3} wrap="wrap">
            <Box>
              <Heading size="sm">Recommended equipment</Heading>
              <Text fontSize="13px" color="text.secondary" mt={1}>
                Staff shortlist — villager or vendor owned. Assign after offline
                confirmation.
              </Text>
            </Box>
            <StatusBadge status="neutral">
              {recommendationRows.length} of {maxRecommendations} recommended
            </StatusBadge>
          </Flex>
        </Box>
        <Box overflowX="auto">
          <Table variant="simple" size="md">
            <Thead bg="bg.input">
              <Tr>
                <SortableTh columnKey="assetId" sort={sort} onSort={toggleSort}>
                  Asset
                </SortableTh>
                <SortableTh columnKey="equipment" sort={sort} onSort={toggleSort}>
                  Equipment
                </SortableTh>
                <SortableTh columnKey="ownerType" sort={sort} onSort={toggleSort}>
                  Owner type
                </SortableTh>
                <SortableTh columnKey="owner" sort={sort} onSort={toggleSort}>
                  Owner
                </SortableTh>
                <SortableTh columnKey="village" sort={sort} onSort={toggleSort}>
                  Village
                </SortableTh>
                <SortableTh columnKey="operational" sort={sort} onSort={toggleSort}>
                  Current status
                </SortableTh>
                <SortableTh columnKey="remark" sort={sort} onSort={toggleSort}>
                  Remark
                </SortableTh>
                <Th>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {sortedRecommendations.map(({ rec, asset, owner }) => {
                const isSelected =
                  indent.assignment?.assetId === rec.assetId ||
                  rec.status === 'Selected';
                return (
                  <Tr key={rec.recId} _hover={{ bg: 'bg.input' }}>
                    <Td
                      fontFamily="mono"
                      fontSize="13px"
                      cursor="pointer"
                      color="accent.amber"
                      fontWeight="600"
                      onClick={() =>
                        navigate(`/admin/equipments/fleet/${rec.assetId}`, {
                          state: {
                            from: `/admin/equipments/indents/${indent.indentId || indentId}`,
                          },
                        })
                      }
                    >
                      {rec.assetId}
                    </Td>
                    <Td>
                      <Text fontWeight="600" fontSize="14px">
                        {asset?.equipmentDescription ||
                          asset?.equipmentType ||
                          rec.equipmentName ||
                          '—'}
                      </Text>
                      {(asset?.equipmentDescription || rec.equipmentName) &&
                      (asset?.equipmentType || rec.equipmentType) &&
                      (asset?.equipmentDescription || rec.equipmentName) !==
                        (asset?.equipmentType || rec.equipmentType) ? (
                        <Text fontSize="12px" color="text.secondary">
                          Type: {asset?.equipmentType || rec.equipmentType}
                        </Text>
                      ) : null}
                      <Text fontSize="12px" color="text.secondary">
                        {[asset?.manufacturer || rec.manufacturer, asset?.model || rec.model]
                          .filter(Boolean)
                          .join(' ')}
                        {(asset?.regNo || rec.regNo)
                          ? ` · ${asset?.regNo || rec.regNo}`
                          : ''}
                      </Text>
                      {asset?.capacity ? (
                        <Text fontSize="12px" color="text.secondary">
                          Cap: {asset.capacity} {asset.capacityUom}
                        </Text>
                      ) : null}
                      {asset?.remarks ? (
                        <Text fontSize="12px" color="text.secondary" noOfLines={2}>
                          {asset.remarks}
                        </Text>
                      ) : null}
                    </Td>
                    <Td>
                      <StatusBadge
                        status={owner.ownerType === 'Vendor' ? 'info' : 'pending'}
                      >
                        {owner.ownerType}
                      </StatusBadge>
                    </Td>
                    <Td>
                      <Text
                        fontWeight="600"
                        fontSize="14px"
                        cursor={owner.ownerPath ? 'pointer' : 'default'}
                        color={owner.ownerPath ? 'accent.amber' : undefined}
                        onClick={() => {
                          if (owner.ownerPath) navigate(owner.ownerPath);
                        }}
                      >
                        {owner.ownerName}
                      </Text>
                      <Text fontSize="12px" color="text.secondary" fontFamily="mono">
                        {owner.ownerId}
                      </Text>
                      {owner.mobile ? (
                        <Text fontSize="12px" color="text.secondary">
                          {owner.mobile}
                        </Text>
                      ) : null}
                    </Td>
                    <Td fontSize="13px">{owner.villageName || '—'}</Td>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <StatusBadge
                          status={operationalBadge(asset?.operationalStatus)}
                        >
                          {asset?.operationalStatus || '—'}
                        </StatusBadge>
                        <StatusBadge
                          status={
                            asset?.status === 'Verified' ? 'verified' : 'pending'
                          }
                        >
                          KYC: {asset?.status || '—'}
                        </StatusBadge>
                      </VStack>
                    </Td>
                    <Td fontSize="13px" maxW="220px">
                      {rec.remark ? (
                        <Text whiteSpace="pre-wrap">{rec.remark}</Text>
                      ) : (
                        <Text color="text.secondary">—</Text>
                      )}
                      {rec.note && rec.note !== rec.remark ? (
                        <Text fontSize="12px" color="text.secondary" mt={1} noOfLines={3}>
                          {rec.note}
                        </Text>
                      ) : null}
                    </Td>
                    <Td>
                      <VStack align="start" spacing={1}>
                        {isSelected ? (
                          <>
                            <StatusBadge status="verified">Given work</StatusBadge>
                            <StatusBadge status="info">
                              {indent.assignment?.assignedByActorRaw === 'vendor_portal' ||
                              indent.assignment?.assignedByActor === 'Vendor'
                                ? 'Given by Vendor'
                                : 'Given by Team'}
                            </StatusBadge>
                            {indent.assignment?.assignedBy ? (
                              <Text fontSize="11px" color="text.secondary" noOfLines={1}>
                                {indent.assignment.assignedBy}
                              </Text>
                            ) : null}
                          </>
                        ) : canAssign ? (
                          <PrimaryButton
                            size="sm"
                            isDisabled={busy || asset?.operationalStatus === 'Deployed'}
                            onClick={() => onAssign(rec.assetId)}
                          >
                            Assign
                          </PrimaryButton>
                        ) : (
                          <Text fontSize="13px" color="text.secondary">
                            —
                          </Text>
                        )}
                        {canRecommend && !isSelected ? (
                          <IconButton
                            aria-label="Remove recommendation"
                            icon={<MdClose />}
                            size="sm"
                            variant="ghost"
                            onClick={() => onRemoveRecommendation(rec.recId)}
                          />
                        ) : null}
                      </VStack>
                    </Td>
                  </Tr>
                );
              })}
              {recommendationRows.length === 0 ? (
                <Tr>
                  <Td colSpan={8}>
                    <Text color="text.secondary" py={8} textAlign="center">
                      No recommendations yet. Click Find & recommend.
                    </Text>
                  </Td>
                </Tr>
              ) : null}
            </Tbody>
          </Table>
        </Box>
      </AppCard>

      {indent.assignment ? (
        <AppCard mb={6}>
          <Heading size="sm" mb={4}>
            Assignment (Given To)
          </Heading>
          <SimpleGrid columns={DETAIL_COLUMNS} spacing={3}>
            <DetailRow label="Asset" value={indent.assignment.assetId} />
            <DetailRow label="Owner type" value={assignedOwner?.ownerType} />
            <DetailRow label="Owner" value={assignedOwner?.ownerName} />
            <DetailRow label="Mobile" value={assignedOwner?.mobile} />
            <DetailRow label="Village" value={assignedOwner?.villageName} />
            <DetailRow
              label="Assigned at"
              value={formatIndianDateTime(indent.assignment.assignedAt) || '—'}
            />
            <DetailRow
              label="Assigned by"
              value={
                [
                  indent.assignment.assignedByActor || null,
                  indent.assignment.assignedBy || null,
                ]
                  .filter(Boolean)
                  .join(' · ') || '—'
              }
            />
            <DetailRow label="Note" value={indent.assignment.note} />
            <DetailRow
              label="Released at"
              value={formatIndianDateTime(indent.assignment.releasedAt) || '—'}
            />
            <DetailRow label="Released by" value={indent.assignment.releasedBy} />
          </SimpleGrid>
          {canRelease ? (
            <PrimaryButton
              mt={4}
              size="sm"
              leftIcon={<MdLockOpen />}
              isDisabled={busy}
              onClick={onRelease}
            >
              Release equipment
            </PrimaryButton>
          ) : null}
        </AppCard>
      ) : null}

      <RecommendAssetsModal
        isOpen={pickModal.isOpen}
        onClose={pickModal.onClose}
        indent={indent}
        maxRecommendations={maxRecommendations}
        onSaved={refresh}
      />
    </Box>
  );
}
